/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

///<reference path="../custom-declarations/mediaelement.d.ts"/>
import * as $ from "jquery";
import "mediaelement/full";


import {UiComponent} from "./UiComponent";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {
	UiVideoPlayer_ErrorLoadingEvent,
	UiVideoPlayer_PlayerProgressEvent,
	UiVideoPlayer_PosterImageSize,
	UiVideoPlayerCommandHandler,
	UiVideoPlayerConfig,
	UiVideoPlayerEventSource
} from "../generated/UiVideoPlayerConfig";
import {EventFactory} from "../generated/EventFactory";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import MediaElementPlayer = mejs.MediaElementPlayer;
import {createUiColorCssString} from "./util/CssFormatUtil";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiVideoPlayer extends UiComponent<UiVideoPlayerConfig> implements UiVideoPlayerCommandHandler, UiVideoPlayerEventSource {

	public readonly onPlayerProgress: TeamAppsEvent<UiVideoPlayer_PlayerProgressEvent> = new TeamAppsEvent<UiVideoPlayer_PlayerProgressEvent>(this);
	public readonly onErrorLoading: TeamAppsEvent<UiVideoPlayer_ErrorLoadingEvent> = new TeamAppsEvent<UiVideoPlayer_ErrorLoadingEvent>(this);

	private $componentWrapper: JQuery;
	private mediaPlayer: any;
	private isContentReady: boolean = false;
	private startPlayingWhenReady: boolean = false;
	private jumpToPositionWhenReady: number = 0;
	private destroyed: boolean;

	constructor(config: UiVideoPlayerConfig, context: TeamAppsUiContext) {
		super(config, context);

		if (config.autoPlay) {
			this.startPlayingWhenReady = true;
		}

		const posterImageSizeCssClass = `poster-${UiVideoPlayer_PosterImageSize[config.posterImageSize].toLowerCase()}`;
		this.$componentWrapper = $(
			`<div id="${config.id}" class="UiVideoPlayer ${posterImageSizeCssClass}">
                    <video src="${config.url}" width="100%" height="100%" poster="${config.posterImageUrl || ''}" preload="${config.preload ? 'auto' : 'none'}"></video>
                </div>`);
		this.$componentWrapper.toggleClass("hide-controls", !config.showControls);

		// TODO this is the point where the element was inserted to the DOM

		this.mediaPlayer = new mejs.MediaElementPlayer(this.$componentWrapper.find('video')[0], {
			enablePluginDebug: false,
			plugins: ['fasterslower'],
			type: '',
			silverlightName: 'silverlightmediaelement.xap',
			features: ['playpause', 'current', 'progress', 'duration', 'tracks', 'volume', 'fullscreen'],
			timerRate: 250,
			success: (mediaElement: HTMLMediaElement) => {
				this.isContentReady = true;

				mediaElement.addEventListener('play', (e) => {
					this.onPlayerProgress.fire(EventFactory.createUiVideoPlayer_PlayerProgressEvent(config.id, 0));
				}, false);

				let lastPlayTime = 0;
				mediaElement.addEventListener('timeupdate', (e) => {
					let currentPlayTime = mediaElement.currentTime;
					if (lastPlayTime < currentPlayTime && lastPlayTime % config.sendPlayerProgressEventsEachXSeconds > currentPlayTime % config.sendPlayerProgressEventsEachXSeconds) {
						this.onPlayerProgress.fire(EventFactory.createUiVideoPlayer_PlayerProgressEvent(config.id, Math.floor(currentPlayTime)));
					}
					lastPlayTime = currentPlayTime;
				}, false);
			},
			error: () => {
				if (!this.destroyed) {
					console.log();
					this.onErrorLoading.fire(EventFactory.createUiVideoPlayer_ErrorLoadingEvent(config.id))
				}
			}
		});

		this.$componentWrapper.add(this.$componentWrapper.find('.mejs__container'))
			.css('background-color', createUiColorCssString(config.backgroundColor));
	}

	public getMainDomElement(): JQuery {
		return this.$componentWrapper;
	}

	protected onAttachedToDom(): void {
		if (this.startPlayingWhenReady) {
			this.play();
		}
	}

	public play() {
		if (this.isContentReady) {
			this.mediaPlayer.play();
		} else {
			this.startPlayingWhenReady = true;
		}
	}

	public pause() {
		this.mediaPlayer.pause();
		this.startPlayingWhenReady = false;
	}

	public jumpTo(seconds: number) {
		if (this.isContentReady) {
			this.mediaPlayer.setCurrentTime(seconds);
		} else {
			this.jumpToPositionWhenReady = seconds;
		}
	}

	public onResize(): void {
		this.mediaPlayer.setPlayerSize(this.mediaPlayer.width, this.mediaPlayer.height); // CAUTION: maybe we will have to handle fullscreen mode
		this.mediaPlayer.setControlsSize();
	}

	public destroy(): void {
		this.destroyed = true;
		try {
			this.mediaPlayer.pause();
			this.mediaPlayer.setSrc("");
			this.mediaPlayer.load();
		} catch (e) {
			this.logger.error("Error while destroying video player: " + e.toString());
		}
	}

	setUrl(url: string): void {
		this.getMainDomElement().find(".mejs__overlay-error").parent().hide();
		this.getMainDomElement().find(".mejs__poster").show();
		this.getMainDomElement().find(".mejs__overlay-play").show();
		this.mediaPlayer.setSrc(url);
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiVideoPlayer", UiVideoPlayer);